<template>
    <v-row
        class="align-center"
        no-gutters
        :class="{'flex-grow-0': !isEditMode}"
        :style="isEditMode ? 'max-width: 350px' : ''"
    >
        <div class="font-weight-regular text-caption me-1">{{ $t('campaign') }}:</div>
        <template v-if="!isEditMode">
            <v-progress-circular v-if="loading" indeterminate size="24" color="grey" />
            <div v-else class="d-flex align-center">
                <span class="me-1">
                    <span v-if="currentCampaign" class="text-subtitle-2">{{ currentCampaign.title }}</span>
                </span>
                <v-btn color="grey" size="small" variant="text" icon="mdi-pencil" data-cy="edit" @click="toggleMode" />
            </div>
        </template>
        <div v-else class="d-flex flex-grow-1 align-center">
            <v-select
                v-model="campaignId"
                :items="campaigns"
                :placeholder="$t('campaigns')"
                clearable
                density="compact"
                color="primary"
                data-cy="campaign-id"
                hide-details
                max-width="350"
            />
            <v-btn
                :loading="loading"
                icon="mdi-check-circle-outline"
                variant="text"
                color="success"
                class="ms-1 cursor-pointer"
                data-cy="send"
                @click="send"
            />
        </div>
    </v-row>
</template>

<script setup lang="ts">
    import type {SelectOptionType} from '~/utils/types/types'

    const order = ref()
    const loading = ref<boolean>(false)
    const isEditMode = ref<boolean>(false)
    const campaigns = ref<SelectOptionType[]>([])
    const campaignId = ref<number>()
    const currentCampaign = ref<SelectOptionType>()

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        order.value = runtimeStore.orderDetails
        campaignId.value = order.value?.campaignId ? order.value.campaignId : null

        try {
            loading.value = true
            const resp = await app.$api.campaigns.get()
            campaigns.value = resp.campaigns.map(({campaignId, name}) => ({
                value: campaignId,
                title: name,
            }))
            currentCampaign.value = order.value?.campaignId
                ? campaigns.value.find(({value}) => value === order.value.campaignId)
                : null
        } catch (e) {
            campaigns.value = []
        } finally {
            loading.value = false
        }
    })

    const send = async (): Promise<void> => {
        try {
            loading.value = true
            currentCampaign.value = campaignId.value
                ? campaigns.value.find(({value}) => value === campaignId.value)
                : null
            const data = {campaignId: campaignId.value ? campaignId.value : 0}

            await app.$api.orders.put(order.value.orderId, data)

            runtimeStore.SET_ORDER_DETAILS({...order.value, ...data})
            order.value = runtimeStore.orderDetails
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('data_update_fail'))
        } finally {
            loading.value = false
            toggleMode()
        }
    }

    function toggleMode(): void {
        isEditMode.value = !isEditMode.value
    }
</script>
